import { ImageUploadInput, ImageUploadPreview } from "@base/ui/ImageUpload";
import { VirtualizedImageGrid } from "@base/ui/VirtualizedImageGrid";
import { ImagePreview } from "@features/ImagePreview";
import type { ImageItem } from "../model/imageItem";

interface ImageGalleryProps {
  /** seq 정렬된 단일 아이템 배열 (existing + uploading + uploaded). */
  items: ImageItem[];
  isAtMax: boolean;
  maxImages: number;
  onAddFiles: (files: FileList | File[]) => void;
  onRemove: (id: string) => void;
  onRetry: (id: string) => void;
  onLocalPreviewReady: (id: string, localPreviewUrl: string) => void;
}

/**
 * 기존 이미지 + 업로드 중 이미지 + 이번 세션 완료 이미지를 단일 그리드로 보여준다.
 *
 * 입력은 seq 정렬된 단일 `ImageItem[]` 이므로, 과거의 preExisting/merged 분리 병합이 사라진다.
 * 셀은 lifecycle 로 디스패치한다:
 * - uploading → {@link ImageUploadPreview} (진행률/에러 오버레이)
 * - existing / uploaded → {@link ImagePreview} (webp 우선 + 원본 폴백)
 */
export function ImageGallery({
  items,
  isAtMax,
  maxImages,
  onAddFiles,
  onRemove,
  onRetry,
  onLocalPreviewReady,
}: ImageGalleryProps) {
  return (
    <div>
      <label htmlFor="site-picture-upload" className="text-label2">
        Upload image
        <span aria-hidden className="text-red-500">
          *
        </span>{" "}
        (Max {maxImages} images)
      </label>
      <ImageUploadInput onSelect={onAddFiles} className="w-full h-full" disabled={isAtMax} />

      <VirtualizedImageGrid
        className="mt-4"
        itemCount={items.length}
        getItemKey={(index) => items[index]?.id ?? `item-${index}`}
        renderItem={(index) => {
          const item = items[index];
          if (!item) return null;

          if (item.lifecycle === "uploading" && item.upload && item.file && item.localPreviewUrl) {
            return (
              <ImageUploadPreview
                item={{
                  file: item.file,
                  previewUrl: item.localPreviewUrl,
                  status: item.upload.status,
                  progress: item.upload.progress,
                }}
                order={index + 1}
                onRemove={() => onRemove(item.id)}
                onRetry={() => onRetry(item.id)}
              />
            );
          }

          if (!item.originalUrl) return null;
          const localPreviewUrl = item.localPreviewUrl;
          return (
            <ImagePreview
              src={item.originalUrl}
              thumbnailSrc={item.webpUrl}
              isHeic={item.isHeic}
              localPreviewSrc={localPreviewUrl}
              order={index + 1}
              onLocalPreviewReady={localPreviewUrl ? () => onLocalPreviewReady(item.id, localPreviewUrl) : undefined}
              onRemove={() => onRemove(item.id)}
            />
          );
        }}
      />
    </div>
  );
}
